import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, EMPTY, expand, map, reduce } from 'rxjs';

import { environment } from '../../environments/environment';

export interface TenantResponse {
  id: string;
  companyName: string;
  displayName: string;
  contactEmail: string;
  contactName: string;
  contactPhone: string;
  addressStreet: string;
  addressCity: string;
  addressPostalCode: string;
  addressCountry: string;
  consentVersion: string;
  createdAt: string;
  vatId: string; // Story 60.1 — optional, normalised; '' = none
}

/** Partial PATCH body — any subset of the 10 mutable fields. */
export interface TenantPatchRequest {
  companyName?: string;
  displayName?: string;
  contactEmail?: string;
  contactName?: string;
  contactPhone?: string;
  addressStreet?: string;
  addressCity?: string;
  addressPostalCode?: string;
  addressCountry?: string;
  vatId?: string; // '' clears it
}

// Story 24.3 — the closing state returned by DELETE /admin/tenant. `purgeAfter`
// is Unix epoch SECONDS (a number, not a string) — see backend CloseTenantResponse.
export interface CloseTenantResponse {
  status: string;
  closingInitiatedAt: string;
  purgeAfter: number;
}

// Story 29.2 rework — operator UI preferences as a generic string→string map
// (User.Preferences, DynamoDB Map of String; supersedes the Story 25.3 single
// `theme` field). GET returns the full map ({} when unset). PATCH merges the
// provided keys; the backend allowlists keys (`theme`, `view_mode.*`) and
// validates values, rejecting anything else (including any top-level body key
// other than `preferences`).
// ─── Operator MFA (Story 47.1) ───────────────────────────────────────────────

export type MfaMethod = 'totp' | 'email';

export interface MfaStatus {
  enabled: boolean;
  /** The FIRST held method (totp before email) or '' — kept for wire compatibility. */
  method: MfaMethod | '';
  /** Every held method (Story 47.5) — an operator may hold both. */
  methods?: MfaMethod[];
  enrolledAt?: string;
  pendingMethod?: MfaMethod | '';
  recoveryCodesRemaining: number;
  /** The open AC4 re-auth window, if any (RFC 3339). */
}

/** For totp: the otpauth:// URI (QR) + manual-entry secret — the ONLY time the secret is returned. */
export interface MfaEnrollResponse {
  method: MfaMethod;
  otpauthUrl?: string;
  secret?: string;
  codeSent: boolean;
}

/** Recovery codes are returned exactly once, here and from regenerate. */
export interface MfaConfirmResponse {
  enabled: boolean;
  method: MfaMethod;
  recoveryCodes: string[];
}

export interface MfaReauthRequest {
  password: string;
  mfaCode?: string;
  recoveryCode?: string;
}

export interface PreferencesResponse {
  preferences: Record<string, string>;
}

export interface PreferencesPatchRequest {
  preferences: Record<string, string>;
}

// Story 25.5 — operator-editable login-text overrides. Open map lang→key→text.
// Both Environment-level (defaults) and Application-level (overrides) endpoints
// share this shape; GET and PUT bodies are both { i18n: I18nMap }.
export type I18nMap = Record<string, Record<string, string>>;

export interface I18nResponse {
  i18n: I18nMap;
}

// Story 30.1/30.4 — custom page DTO (camelCase per D13 convention).
export interface CustomPageDto {
  environmentId: string;
  pageType: string;
  html: string;
  css: string;
  js: string;
  published: boolean;
  publishedAt?: string;
  updatedAt: string;
  updatedBy: string;
}

// Story 31.1/31.3 — custom email template DTO (camelCase, mirrors the backend
// TemplateResponse). emailType is one of EmailType (the backend's
// entity.ValidEmailTypes — Story 47.4 added the end-user 'mfa-code').
export type EmailType = 'password-reset' | 'email-verification' | 'mfa-code';

export interface EmailTemplateDto {
  environmentId: string;
  // A real union, not `EmailType | string` — that widened straight back to
  // `string` and gave the DTO exactly the narrowing it had before amendment (j)
  // asked for one (2026-09-05 review).
  emailType: EmailType;
  subject: string;
  htmlBody: string;
  published: boolean;
  publishedAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface EnvironmentResponse {
  id: string;
  name: string;
  isSystem: boolean;
  isProduction: boolean;
  createdAt: string;
  // Story 29.4 — operator-set default/fallback login language ("" = none).
  // Always present (backend serializes it without omitempty) so the detail page
  // can pre-select the Localisations-tab selector.
  defaultLanguage: string;
}

export interface CreateEnvironmentRequest {
  name: string;
}

export interface PatchEnvironmentRequest {
  name?: string;
  isProduction?: boolean;
  // Story 29.4 — "" clears the default; a non-empty code must be a configured
  // i18n language (backend-validated).
  defaultLanguage?: string;
}

// Story 27.2 — custom domain (GET returns this or null).
export interface CustomDomainResponse {
  hostname: string;
  // Story 27.10 — 'removing' is the two-phase teardown state (the tenant is being
  // disabled+deleted by the domain-checker); the panel shows a Removing… state
  // until the env is cleared (status back to no-custom-domain).
  status: 'pending_dns' | 'validating' | 'active' | 'failed' | 'removing';
  routingEndpoint?: string;
  dcvRecordName?: string;
  dcvRecordValue?: string;
  configuredAt?: string;
}

export interface CustomDomainDnsRecord {
  type: string;
  name: string;
  value: string;
}

export interface CustomDomainPutResponse {
  status: string;
  dns_records: CustomDomainDnsRecord[];
  message: string;
}

// Story 35.2 — BYOE email provider (Epic 35). The backend wire format is
// snake_case (35.1 decision — unlike custom-domain's camelCase GET; the
// dns_records precedent above). GET returns this or null; responses NEVER
// carry credentials or the secret ARN.
// ─── Story 49.4 (EO-295) — BYO event bus ─────────────────────────────────────
//
// The provider catalogue is SERVER DATA, not a constant in this file. Epic 49
// made providers database rows precisely so that adding one is a seed row plus
// a relay adapter — not a frontend deploy. Every rule the wizard renders from
// (enum values, integer, length cap, conditional visibility, secret) comes from
// these shapes.

/** One configurable property of a provider, as the registry declares it. */
export interface EventBusProviderProperty {
  name: string;
  /** 'string' | 'integer' | 'enum' | 'https_url' (email registry, Story 53.5) — drives the input control. */
  data_type: string;
  /** Present for 'enum' only; the complete option set. */
  allowed_values?: string[];
  max_length: number;
  /**
   * When set, this property is mandatory ONLY while the named sibling holds
   * conditional_on_value, and must be ABSENT otherwise. The server enforces
   * both halves, so a wizard that ignores this produces a 400 the operator
   * cannot act on.
   */
  conditional_on_property?: string;
  conditional_on_value?: string;
  /**
   * A credential. Renders as a password input and is WRITE-ONLY: the GET never
   * returns one, so it is never pre-filled on edit.
   */
  is_secret: boolean;
}

export interface EventBusProviderCatalogueEntry {
  name: string;
  properties: EventBusProviderProperty[];
}

export interface EventBusProviderCatalogue {
  providers: EventBusProviderCatalogueEntry[];
}

/** GET/PUT state. Structurally cannot carry a credential — see the Go twin. */
export interface EventBusState {
  provider: string;
  properties: Record<string, string>;
  configured_at: string;

  // Story 49.4 AC2 — delivery health, recorded by the relay on every outcome.
  //
  // EMPTY MEANS NEVER for both timestamps, and is NOT the same as a zero time.
  // A configured bus that has never delivered must not render as healthy — the
  // green-dashboard-zero-backups lesson FR64 cites by name.
  //
  // last_error is already sanitized server-side and is a deliberate
  // tenant-visible disclosure surface. Bind it as TEXT; never as innerHTML.
  //
  // Optional on the type because a PUT response is built before any delivery
  // has happened, and because an older server would omit them entirely.
  last_success_at?: string;
  last_error_at?: string;
  last_error?: string;
  // Story 53.8 — the error's class, from the relay's closed, add-only
  // vocabulary (backend/event-relay/internal/adapter/errorkind.go; mirrored in
  // pages/environments/event-bus-errors.ts). Typed `string`, not the union, on
  // purpose: a newer server may send a kind this build does not know, and that
  // must render as "unknown" (the provider text alone), not fail to type-check
  // its way into a crash. Omitted by the server when empty or "unknown".
  last_error_kind?: string;
  consecutive_failures: number;
}

export interface PutEventBusRequest {
  provider: string;
  /** One flat bag; the server splits secret from stored using is_secret. */
  properties: Record<string, string>;
}

// Story 53.5 — BYOE is registry-driven like the event bus: the provider set and
// each provider's properties come from GET /email-provider/providers (the same
// catalogue shape, EventBusProviderCatalogue), not from types here.

/** GET/PUT state. `properties` holds only NON-SECRET values; never a credential. */
export interface EmailProviderState {
  provider: string;
  sender_address: string;
  properties: Record<string, string>;
  configured_at: string;
}

/** PUT / test body: one flat bag; the server splits secret from stored using is_secret. */
export interface PutEmailProviderRequest {
  provider: string;
  sender_address: string;
  properties: Record<string, string>;
}

export interface ApplicationResponse {
  clientId: string;
  name: string;
  environmentId: string;
  isSystem: boolean;
  allowUserSignup: boolean;
  oidcEnabled: boolean;
  requireCaptcha: boolean;
  requireEmailVerification?: boolean;
  /** Story 47.2 — per-application end-user MFA requirement (step-up at /authorize). */
  requireMfa?: boolean;
  /** Story 47.6 — the methods this application accepts; [] = unrestricted (both). */
  allowedMfaMethods?: ('totp' | 'email')[];
  allowedGrantTypes: string[];
  callbackUrl: string[];
  allowedCorsOrigins: string[];
  returnUrl: string;
  audiences: Record<string, string[]>;
}

export interface AudienceResponse {
  id: string;
  identifier: string;
  name: string;
  scopes: string[];
  environmentId: string;
  isSystem: boolean;
  accessTokenTtl: number;
  idTokenTtl: number;
}

export interface CreateApplicationRequest {
  clientId: string;
  name: string;
  allowedMfaMethods?: ('totp' | 'email')[];
  allowedGrantTypes?: string[];
  callbackUrl?: string[];
  allowedCorsOrigins?: string[];
  returnUrl?: string;
}

export interface PatchApplicationRequest {
  name?: string;
  allowUserSignup?: boolean;
  oidcEnabled?: boolean;
  requireCaptcha?: boolean;
  requireEmailVerification?: boolean;
  requireMfa?: boolean;
  allowedMfaMethods?: ('totp' | 'email')[];
  allowedGrantTypes?: string[];
  callbackUrl?: string[];
  allowedCorsOrigins?: string[];
  returnUrl?: string;
  audiences?: Record<string, string[]>;
}

export interface CreateAudienceRequest {
  identifier: string;
  name: string;
  scopes: string[];
  accessTokenTtl?: number;
  idTokenTtl?: number;
}

export interface PatchAudienceRequest {
  name?: string;
  scopes?: string[];
  accessTokenTtl?: number;
  idTokenTtl?: number;
}

export interface UserResponse {
  userId: string;
  email: string;
  environmentId: string;
  blocked: boolean;
  failedLoginAttempts: number;
  name?: string;
  givenName?: string;
  familyName?: string;
  preferredUsername?: string;
  picture?: string;
  locale?: string;
  zoneinfo?: string;
  emailVerified?: boolean;
  createdAt?: string;
  clients: Record<string, Record<string, string[]>>;
  // Story 47.2 AC8 — read-only second-factor projection (never the secret).
  // Story 47.5: mfaMethods is every held method; mfaMethod stays the first.
  mfaEnabled?: boolean;
  mfaMethod?: 'totp' | 'email' | '';
  mfaMethods?: ('totp' | 'email')[];
}

export interface CreateUserRequest {
  email: string;
  password: string;
  // Client bindings (client_id → audience → scopes). 37.21: the create-user
  // modal now requires exactly one client with an empty scope map, so the
  // user's verification-email token FK-references a real client.
  clients?: Record<string, Record<string, string[]>>;
}

export interface PatchUserRequest {
  name?: string;
  givenName?: string;
  familyName?: string;
  preferredUsername?: string;
  picture?: string;
  locale?: string;
  zoneinfo?: string;
  emailVerified?: boolean;
  blocked?: boolean;
  failedLoginAttempts?: number;
  clients?: Record<string, Record<string, string[]>>;
}

export interface AdminErrorResponse {
  error: string;
  errorDescription: string;
  errorUri: string;
}

// Operator invitations (Story 21.1 backend; Story 21.2 UI). The invitee shows
// up in the operators list only after accepting (Story 21.3).
export interface InviteOperatorRequest {
  email: string;
  // Optional scope subset. Omitted at MVP → backend defaults to the inviter's
  // full scope set (equal-privilege admin).
  grantedScopes?: string[];
}

// Story 34.1 AC9 — the token is NEVER returned to the operator (the accept link
// is delivered by email). Create returns only the expiry.
export interface InviteOperatorResponse {
  expiresAt: string;
}

// Story 21.3 — the public invitation-accept page. Anonymous (the invitee has no
// session); these hit the operators handler's anonymous GET/POST routes.
export interface InvitationInfoResponse {
  tenantDisplayName: string;
  invitedEmail: string;
  status: string;
  expiresAt: string;
}

export interface AcceptInvitationResponse {
  adminClientId: string;
  userId: string;
}

// Story 21.4 — one row of the authenticated pending-invitations list. Story 34.1
// AC9: the token is NO LONGER exposed (the operator never sees the accept URL);
// revoke keys off the invited email.
export interface InvitationSummary {
  invitedEmail: string;
  status: string; // 'pending' | 'expired'
  expiresAt: string;
  createdAt: string;
}

// Story 38.6 — compliance re-acceptance modal (AC1/AC2) and consent-settings page (AC3).
export interface ComplianceDoc {
  id: string;
  number: string;
  kind: 'term' | 'permission';
  version: number;
  resume: string;
  fullText: string;
}

// ConsentPermission extends ComplianceDoc with the operator's current decision.
// decision is null when the operator has never responded to this permission's
// latest active version (distinguishes "never decided" from "declined").
export interface ConsentPermission extends ComplianceDoc {
  decision: 'accepted' | 'declined' | null;
}

export interface AcceptanceDecision {
  termId: string;
  decision: 'accepted' | 'declined';
}

export interface AcceptancesRequest {
  decisions: AcceptanceDecision[];
}

export interface AcceptancesResult {
  status: 'active' | 'suspended';
  consentGraceUntil?: number; // epoch seconds, present when suspended
}

// TermAcceptanceStatus is one element of GET /admin/compliance/acceptances.
// decision/recordedAt are null when the operator has never responded to this
// document's latest active version.
export interface TermAcceptanceStatus {
  number: string;
  kind: 'term' | 'permission';
  version: number;
  resume: string;
  decision: 'accepted' | 'declined' | null;
  recordedAt: string | null;
}

export interface AcceptedTermHistoryItem {
  number: string;
  kind: 'term';
  version: number;
  resume: string;
  recordedAt: string;
}

// Story 38.7 — terms management + per-client eligibility picker (AC1–AC3).
export interface TermListItem {
  number: string;
  kind: 'term' | 'permission';
  version: number;
  resume: string;
  activeFrom?: string;
  activeTo?: string;
  createdAt: string;
}

export interface TermVersion {
  id: string;
  number: string;
  kind: 'term' | 'permission';
  version: number;
  resume: string;
  fullText: string;
  activeFrom?: string;
  activeTo?: string;
  createdAt: string;
}

export interface TermDocument {
  number: string;
  kind: 'term' | 'permission';
  versions: TermVersion[];
}

export interface CreateTermRequest {
  number: string;
  kind: 'term' | 'permission';
  resume: string;
  fullText: string;
  activeFrom?: string;
  activeTo?: string;
}

export interface PublishVersionRequest {
  resume: string;
  fullText: string;
  activeFrom?: string;
  activeTo?: string;
}

export interface Eligibility {
  clientIds: string[];
}

// Story 26.3 — log query API (Story 26.2 backend). The viewer sends snake_case
// query params and the backend returns snake_case JSON; the service maps both
// to/from camelCase so components stay idiomatic.
export interface LogQueryParams {
  startTime: string; // ISO 8601 RFC3339 → start_time
  endTime: string; // ISO 8601 RFC3339 → end_time
  limit?: number; // 1–500; omit to use backend default (100)
  nextToken?: string; // pagination cursor → next_token; omit on first load
  level?: 'INFO' | 'WARN' | 'ERROR'; // Story 29.6 — severity filter → level; omit for ALL
  correlationId?: string; // Story 49.6 — exact flow id → correlation_id
  reason?: string; // Story 49.6 — closed rejection code → reason
  search?: string; // Story 53.4 — free text, matched case-insensitively → search
}

// Story 49.6 AC5 — the closed rejection vocabulary (eventbus/reason.go). Mirrored
// here for the console's filter dropdown; the backend allow-lists it again.
export const LOG_REASONS: readonly string[] = [
  'invalid_request', 'internal_error', 'client_unknown', 'client_secret_invalid', 'origin_not_allowed',
  'unsupported_grant_type', 'unauthorized_client', 'redirect_uri_mismatch', 'audience_unknown', 'invalid_scope',
  'tenant_closed', 'consent_suspended', 'terms_pending', 'consent_declined', 'tenant_mismatch',
  'session_invalid', 'user_not_found', 'invalid_password', 'account_blocked', 'email_not_verified',
  'password_migration_required', 'captcha_failed', 'captcha_unavailable', 'weak_password', 'account_exists',
  'signup_disabled', 'access_denied_unbound', 'access_denied_admin_client', 'too_many_attempts', 'mfa_lockout',
  'code_invalid', 'code_expired', 'code_replayed', 'pkce_mismatch', 'exchange_mismatch',
  'refresh_token_invalid', 'refresh_token_replayed', 'refresh_token_client_mismatch',
  'device_code_invalid', 'device_code_expired', 'device_code_denied', 'device_code_replayed', 'device_code_client_mismatch',
  'reset_token_invalid', 'verification_token_invalid', 'already_verified', 'email_dispatch_failed',
  'mfa_code_invalid', 'mfa_not_enrolled', 'mfa_already_enrolled', 'mfa_method_not_allowed', 'step_up_required',
  'enrollment_not_pending',
];

export interface LogEntry {
  timestamp: string;
  logGroup?: string;
  level?: string;
  tenantId?: string;
  environmentId?: string;
  clientId?: string;
  userId?: string;
  correlationId?: string; // Story 49.6 — the flow this line belongs to
  reason?: string; // Story 49.6 — the rejection's closed code, when it is one
  msg: string;
  raw: Record<string, unknown>;
}

export interface LogsResponse {
  items: LogEntry[];
  nextToken?: string; // absent (key omitted) when no more data — NOT null
}

// Wire shape (snake_case) as returned by the Story 26.2 backend.
interface RawLogEntry {
  timestamp?: string;
  log_group?: string;
  level?: string;
  tenant_id?: string;
  environment_id?: string;
  client_id?: string;
  user_id?: string;
  correlation_id?: string;
  reason?: string;
  msg?: string;
  raw?: Record<string, unknown>;
}

interface RawLogsResponse {
  items: RawLogEntry[];
  next_token?: string;
}

function mapLogsResponse(r: RawLogsResponse): LogsResponse {
  return {
    items: (r.items ?? []).map((e) => ({
      timestamp: e.timestamp ?? '',
      logGroup: e.log_group,
      level: e.level,
      tenantId: e.tenant_id,
      environmentId: e.environment_id,
      clientId: e.client_id,
      userId: e.user_id,
      correlationId: e.correlation_id,
      reason: e.reason,
      msg: e.msg ?? '',
      raw: e.raw ?? {},
    })),
    nextToken: r.next_token,
  };
}

// Story 29.10 — server-side users search + pagination.
export interface UserSearchParams {
  email?: string; // case-insensitive PREFIX term (begins_with on email; lowercased server-side)
  limit?: number; // positive int; backend default 100, max 500
  nextToken?: string; // opaque base64 cursor from a prior page
}

// The {users, nextToken} envelope returned by GET .../users. nextToken is
// absent (key omitted) on the last page — NOT null (mirrors LogsResponse).
export interface UsersResponse {
  users: UserResponse[];
  nextToken?: string;
}

// The users endpoint already returns camelCase ({users, nextToken}), so no
// snake→camel remap is needed (unlike the logs endpoints). Query params,
// however, are snake_case on the wire (limit / next_token / email).
function usersHttpParams(params: UserSearchParams): HttpParams {
  let httpParams = new HttpParams();
  if (params.email) {
    httpParams = httpParams.set('email', params.email);
  }
  if (params.limit) {
    httpParams = httpParams.set('limit', params.limit);
  }
  if (params.nextToken) {
    httpParams = httpParams.set('next_token', params.nextToken);
  }
  return httpParams;
}

function logHttpParams(params: LogQueryParams): HttpParams {
  let httpParams = new HttpParams().set('start_time', params.startTime).set('end_time', params.endTime);
  if (params.limit) {
    httpParams = httpParams.set('limit', params.limit);
  }
  if (params.nextToken) {
    httpParams = httpParams.set('next_token', params.nextToken);
  }
  if (params.level) {
    httpParams = httpParams.set('level', params.level);
  }
  if (params.correlationId) {
    httpParams = httpParams.set('correlation_id', params.correlationId);
  }
  if (params.reason) {
    httpParams = httpParams.set('reason', params.reason);
  }
  if (params.search) {
    httpParams = httpParams.set('search', params.search);
  }
  return httpParams;
}

/**
 * TenantAdminApiService — wraps the /admin/* endpoints. The Bearer header is
 * auto-injected by `adminApiInterceptor`; this service just exposes typed
 * call shapes.
 *
 * Note: the pre-RP4-v2 exchangeCodeForTokens helper was deleted in the
 * Round 2 (Opus RP7 2026-05-23) code review — under RP4-v2 the admin-login
 * Lambda exchanges PKCE codes server-side, so the SPA never POSTs to
 * /oauth/token. See the deleted DN1 entry in the story spec.
 */
@Injectable({ providedIn: 'root' })
export class TenantAdminApiService {
  private readonly http = inject(HttpClient);

  fetchTenant(): Observable<TenantResponse> {
    return this.http.get<TenantResponse>(`${environment.tenantAdminApiUrl}/admin/tenant`);
  }

  patchTenant(patch: TenantPatchRequest): Observable<TenantResponse> {
    return this.http.patch<TenantResponse>(`${environment.tenantAdminApiUrl}/admin/tenant`, patch);
  }

  // Story 24.3 — soft-close the operator's own tenant. DELETE /admin/tenant
  // (requires tenant:delete) returns the closing state. `purgeAfter` is epoch
  // SECONDS (a JSON number), matching the backend CloseTenantResponse.
  closeTenant(): Observable<CloseTenantResponse> {
    return this.http.delete<CloseTenantResponse>(`${environment.tenantAdminApiUrl}/admin/tenant`);
  }

  // Story 25.4 — self-scoped operator preferences (Story 25.3 backend). The
  // signed-in operator reads/writes only their own preferences (keyed on the
  // JWT claims server-side; no id in the path or body).
  fetchPreferences(): Observable<PreferencesResponse> {
    return this.http.get<PreferencesResponse>(`${environment.tenantAdminApiUrl}/admin/preferences`);
  }

  patchPreferences(patch: PreferencesPatchRequest): Observable<PreferencesResponse> {
    return this.http.patch<PreferencesResponse>(`${environment.tenantAdminApiUrl}/admin/preferences`, patch);
  }

  // ─── Operator MFA (Story 47.1) — self-scoped on the JWT, no ids in the URL ──

  getMfaStatus(): Observable<MfaStatus> {
    return this.http.get<MfaStatus>(`${environment.tenantAdminApiUrl}/admin/mfa`);
  }

  startMfaEnrollment(method: MfaMethod): Observable<MfaEnrollResponse> {
    return this.http.post<MfaEnrollResponse>(`${environment.tenantAdminApiUrl}/admin/mfa/enroll`, { method });
  }

  resendMfaEnrollmentCode(): Observable<{ codeSent: boolean }> {
    return this.http.post<{ codeSent: boolean }>(`${environment.tenantAdminApiUrl}/admin/mfa/enroll/resend`, {});
  }

  cancelMfaEnrollment(): Observable<{ cancelled: boolean }> {
    return this.http.delete<{ cancelled: boolean }>(`${environment.tenantAdminApiUrl}/admin/mfa/enroll`);
  }

  confirmMfaEnrollment(code: string): Observable<MfaConfirmResponse> {
    return this.http.post<MfaConfirmResponse>(`${environment.tenantAdminApiUrl}/admin/mfa/confirm`, { code });
  }

  /** AC4 — password + current second factor (or a recovery code) opens a short re-auth window. */
  mfaReauth(body: MfaReauthRequest): Observable<{ reauthValidUntil: string }> {
    return this.http.post<{ reauthValidUntil: string }>(`${environment.tenantAdminApiUrl}/admin/mfa/reauth`, body);
  }

  sendMfaReauthCode(): Observable<{ codeSent: boolean }> {
    return this.http.post<{ codeSent: boolean }>(`${environment.tenantAdminApiUrl}/admin/mfa/reauth/send-code`, {});
  }

  regenerateMfaRecoveryCodes(): Observable<{ recoveryCodes: string[] }> {
    return this.http.post<{ recoveryCodes: string[] }>(`${environment.tenantAdminApiUrl}/admin/mfa/recovery-codes/regenerate`, {});
  }

  disableMfa(): Observable<MfaStatus> {
    return this.http.delete<MfaStatus>(`${environment.tenantAdminApiUrl}/admin/mfa`);
  }

  /** Story 47.5 AC6 — drop ONE held method (re-auth gated; the server refuses the last one). */
  removeMfaMethod(method: MfaMethod): Observable<MfaStatus> {
    return this.http.delete<MfaStatus>(`${environment.tenantAdminApiUrl}/admin/mfa/methods/${method}`);
  }

  listEnvironments(): Observable<EnvironmentResponse[]> {
    return this.http.get<EnvironmentResponse[]>(`${environment.tenantAdminApiUrl}/admin/environments`);
  }

  createEnvironment(req: CreateEnvironmentRequest): Observable<EnvironmentResponse> {
    return this.http.post<EnvironmentResponse>(`${environment.tenantAdminApiUrl}/admin/environments`, req);
  }

  patchEnvironment(id: string, req: PatchEnvironmentRequest): Observable<EnvironmentResponse> {
    return this.http.patch<EnvironmentResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${id}`, req);
  }

  deleteEnvironment(id: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${environment.tenantAdminApiUrl}/admin/environments/${id}`);
  }

  // Story 25.5 — Environment-level login-text overrides (defaults). Whole-map PUT.
  fetchEnvironmentI18n(envId: string): Observable<I18nResponse> {
    return this.http.get<I18nResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/i18n`);
  }

  putEnvironmentI18n(envId: string, i18n: I18nMap): Observable<I18nResponse> {
    return this.http.put<I18nResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/i18n`, { i18n });
  }

  // Story 27.2 — custom domain. GET returns the current state or null; PUT
  // provisions the CloudFront distribution tenant and returns the DNS records.
  getCustomDomain(envId: string): Observable<CustomDomainResponse | null> {
    return this.http.get<CustomDomainResponse | null>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/custom-domain`);
  }

  putCustomDomain(envId: string, hostname: string): Observable<CustomDomainPutResponse> {
    return this.http.put<CustomDomainPutResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/custom-domain`, { hostname });
  }

  /** Story 27.7 — Phase 2: activate the custom domain (DNS pre-check → CreateDistributionTenant). */
  activateCustomDomain(envId: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/custom-domain/activate`, {});
  }

  /** Story 27.3 AC7 — remove the custom domain (tears down the CloudFront tenant). */
  deleteCustomDomain(envId: string): Observable<{ status: string }> {
    return this.http.delete<{ status: string }>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/custom-domain`);
  }

  // Story 35.2 — BYOE email provider. GET returns the current state or null.
  getEmailProvider(envId: string): Observable<EmailProviderState | null> {
    return this.http.get<EmailProviderState | null>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/email-provider`);
  }

  /** Story 53.5 — the email-provider registry catalogue the wizard renders from. */
  getEmailProviders(envId: string): Observable<EventBusProviderCatalogue> {
    return this.http.get<EventBusProviderCatalogue>(
      `${environment.tenantAdminApiUrl}/admin/environments/${envId}/email-provider/providers`);
  }

  // ─── Story 49.4 — BYO event bus ────────────────────────────────────────────

  /**
   * The provider catalogue the wizard renders from.
   *
   * Environment-scoped because the server reuses that gate; the catalogue
   * itself is identical for every Environment and carries no tenant data.
   */
  getEventBusProviders(envId: string): Observable<EventBusProviderCatalogue> {
    return this.http.get<EventBusProviderCatalogue>(
      `${environment.tenantAdminApiUrl}/admin/environments/${envId}/event-bus/providers`);
  }

  /** Current configuration, or null when the environment has no bus. */
  getEventBus(envId: string): Observable<EventBusState | null> {
    return this.http.get<EventBusState | null>(
      `${environment.tenantAdminApiUrl}/admin/environments/${envId}/event-bus`);
  }

  putEventBus(envId: string, body: PutEventBusRequest): Observable<EventBusState> {
    return this.http.put<EventBusState>(
      `${environment.tenantAdminApiUrl}/admin/environments/${envId}/event-bus`, body);
  }

  /**
   * Story 49.4 AC3 — send a synthetic event through the real path.
   *
   * Answers ACCEPTED, not delivered: the relay resolves the configuration,
   * dials the provider and records the outcome afterwards. The health panel is
   * where the result actually shows up.
   */
  testEventBus(envId: string): Observable<{ status: string; message_id: string; event: string }> {
    return this.http.post<{ status: string; message_id: string; event: string }>(
      `${environment.tenantAdminApiUrl}/admin/environments/${envId}/event-bus/test`, {});
  }

  /** Removes the bus and reaps the credential (idempotent server-side). */
  deleteEventBus(envId: string): Observable<{ status: string }> {
    return this.http.delete<{ status: string }>(
      `${environment.tenantAdminApiUrl}/admin/environments/${envId}/event-bus`);
  }

  /** Upserts the provider config; secret properties go to the secret store server-side. */
  putEmailProvider(envId: string, body: PutEmailProviderRequest): Observable<EmailProviderState> {
    return this.http.put<EmailProviderState>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/email-provider`, body);
  }

  /** Removes the provider (idempotent server-side). */
  deleteEmailProvider(envId: string): Observable<{ status: string }> {
    return this.http.delete<{ status: string }>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/email-provider`);
  }

  /**
   * Story 35.5 — test-send: proves the entered credentials work end-to-end.
   * The recipient is server-derived (the authenticated operator's own email);
   * the body carries no recipient field by design.
   */
  testEmailProvider(envId: string, body: PutEmailProviderRequest): Observable<{ sent: boolean; recipient: string }> {
    return this.http.post<{ sent: boolean; recipient: string }>(
      `${environment.tenantAdminApiUrl}/admin/environments/${envId}/email-provider/test`, body);
  }

  listApplications(envId: string): Observable<ApplicationResponse[]> {
    return this.http.get<ApplicationResponse[]>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/applications`);
  }

  getApplication(envId: string, clientId: string): Observable<ApplicationResponse> {
    return this.http.get<ApplicationResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/applications/${clientId}`);
  }

  createApplication(envId: string, req: CreateApplicationRequest): Observable<ApplicationResponse & { clientSecret?: string }> {
    return this.http.post<ApplicationResponse & { clientSecret?: string }>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/applications`, req);
  }

  patchApplication(envId: string, clientId: string, req: PatchApplicationRequest): Observable<ApplicationResponse> {
    return this.http.patch<ApplicationResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/applications/${clientId}`, req);
  }

  deleteApplication(envId: string, clientId: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/applications/${clientId}`);
  }

  rotateApplicationSecret(envId: string, clientId: string): Observable<{ clientId: string; clientSecret: string }> {
    return this.http.post<{ clientId: string; clientSecret: string }>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/applications/${clientId}/rotate-secret`, {});
  }

  // Story 25.5 — Application-level login-text overrides (win over env defaults). Whole-map PUT.
  fetchApplicationI18n(envId: string, clientId: string): Observable<I18nResponse> {
    return this.http.get<I18nResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/applications/${clientId}/i18n`);
  }

  putApplicationI18n(envId: string, clientId: string, i18n: I18nMap): Observable<I18nResponse> {
    return this.http.put<I18nResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/applications/${clientId}/i18n`, { i18n });
  }

  listAudiences(envId: string): Observable<AudienceResponse[]> {
    return this.http.get<AudienceResponse[]>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/audiences`);
  }

  getAudience(envId: string, audienceId: string): Observable<AudienceResponse> {
    return this.http.get<AudienceResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/audiences/${audienceId}`);
  }

  createAudience(envId: string, req: CreateAudienceRequest): Observable<AudienceResponse> {
    return this.http.post<AudienceResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/audiences`, req);
  }

  patchAudience(envId: string, audienceId: string, req: PatchAudienceRequest): Observable<AudienceResponse> {
    return this.http.patch<AudienceResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/audiences/${audienceId}`, req);
  }

  deleteAudience(envId: string, audienceId: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/audiences/${audienceId}`);
  }

  // Story 29.10 — server-side, paginated, case-insensitive email PREFIX search.
  // Returns one page ({users, nextToken}); the caller threads nextToken back to
  // page. The users-list UI drives this directly.
  searchUsers(envId: string, params: UserSearchParams = {}): Observable<UsersResponse> {
    return this.http.get<UsersResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/users`, {
      params: usersHttpParams(params),
    });
  }

  // listUsers preserves its flat-array contract for callers that need the WHOLE
  // set with no pagination UI (operators.component). Post-29.10 the backend only
  // serves the paginated envelope, so this drains every page internally (max
  // page size, follow nextToken) and concatenates. New code that renders a list
  // should prefer searchUsers + a Load-more affordance instead.
  listUsers(envId: string): Observable<UserResponse[]> {
    const pageSize = 500; // backend max — fewest round-trips
    return this.searchUsers(envId, { limit: pageSize }).pipe(
      expand((res) => (res.nextToken ? this.searchUsers(envId, { limit: pageSize, nextToken: res.nextToken }) : EMPTY)),
      reduce((acc, res) => acc.concat(res.users), [] as UserResponse[]),
    );
  }

  getUser(envId: string, userId: string): Observable<UserResponse> {
    return this.http.get<UserResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/users/${userId}`);
  }

  createUser(envId: string, req: CreateUserRequest): Observable<UserResponse> {
    return this.http.post<UserResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/users`, req);
  }

  patchUser(envId: string, userId: string, req: PatchUserRequest): Observable<UserResponse> {
    return this.http.patch<UserResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/users/${userId}`, req);
  }

  deleteUser(envId: string, userId: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/users/${userId}`);
  }

  // Story 28.3 — operator-initiated verification-email resend.
  resendVerificationEmail(envId: string, userId: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/users/${userId}/resend-verification`, {});
  }

  // Story 47.2 AC8 — operator MFA reset for an end user: revokes the user's
  // sessions + refresh tokens, removes the second factor, lifts the lockout.
  resetUserMfa(envId: string, userId: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/users/${userId}/reset-mfa`, {});
  }

  // Story 21.2 — issue an operator invitation. Body omits grantedScopes at MVP
  // (equal-privilege default per the Story 21.1 backend). Story 34.1: the backend
  // emails the accept link and returns only the expiry (no token).
  inviteOperator(email: string, grantedScopes?: string[]): Observable<InviteOperatorResponse> {
    const body: InviteOperatorRequest = grantedScopes && grantedScopes.length > 0 ? { email, grantedScopes } : { email };
    return this.http.post<InviteOperatorResponse>(`${environment.tenantAdminApiUrl}/admin/operators/invite`, body);
  }

  // Story 21.3 — public accept page. Anonymous: validate the invitation link +
  // fetch the inviting tenant's display name (the invitee has no bearer token;
  // the interceptor attaches none when there's no access token).
  getInvitationInfo(token: string): Observable<InvitationInfoResponse> {
    return this.http.get<InvitationInfoResponse>(`${environment.tenantAdminApiUrl}/admin/operators/invitation/${token}`);
  }

  acceptInvitation(token: string, password: string): Observable<AcceptInvitationResponse> {
    return this.http.post<AcceptInvitationResponse>(`${environment.tenantAdminApiUrl}/admin/operators/invitation/${token}/accept`, { password });
  }

  // Story 21.4 — list the tenant's pending (non-consumed) invitations.
  listInvitations(): Observable<InvitationSummary[]> {
    return this.http.get<InvitationSummary[]>(`${environment.tenantAdminApiUrl}/admin/operators/invitations`);
  }

  // Story 34.1 AC9/AC10 — revoke by invited email (the token is never exposed to
  // the operator): DELETE /admin/operators/invitation?email={email}.
  revokeInvitation(email: string): Observable<{ deleted: boolean }> {
    const params = new HttpParams().set('email', email);
    return this.http.delete<{ deleted: boolean }>(`${environment.tenantAdminApiUrl}/admin/operators/invitation`, { params });
  }

  // Story 30.1/30.4 — custom pages CRUD. camelCase DTOs per D13 convention.
  listPages(envId: string): Observable<CustomPageDto[]> {
    return this.http.get<CustomPageDto[]>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/pages`);
  }

  getPage(envId: string, pageType: string): Observable<CustomPageDto> {
    return this.http.get<CustomPageDto>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/pages/${pageType}`);
  }

  savePageDraft(envId: string, pageType: string, body: { html: string; css: string; js: string }): Observable<CustomPageDto> {
    return this.http.put<CustomPageDto>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/pages/${pageType}`, body);
  }

  publishPage(envId: string, pageType: string): Observable<CustomPageDto> {
    return this.http.post<CustomPageDto>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/pages/${pageType}/publish`, {});
  }

  unpublishPage(envId: string, pageType: string): Observable<CustomPageDto> {
    return this.http.post<CustomPageDto>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/pages/${pageType}/unpublish`, {});
  }

  // Story 31.1/31.3 — custom email templates CRUD. camelCase DTOs per D13.
  // Mirrors the pages family: list returns the 2 supported types (defaults
  // synthesized server-side), draft save via PUT, publish/unpublish via POST.
  listEmailTemplates(envId: string): Observable<EmailTemplateDto[]> {
    return this.http.get<EmailTemplateDto[]>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/email-templates`);
  }

  getEmailTemplate(envId: string, emailType: string): Observable<EmailTemplateDto> {
    return this.http.get<EmailTemplateDto>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/email-templates/${emailType}`);
  }

  saveEmailTemplateDraft(envId: string, emailType: string, body: { subject: string; htmlBody: string }): Observable<EmailTemplateDto> {
    return this.http.put<EmailTemplateDto>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/email-templates/${emailType}`, body);
  }

  publishEmailTemplate(envId: string, emailType: string): Observable<EmailTemplateDto> {
    return this.http.post<EmailTemplateDto>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/email-templates/${emailType}/publish`, {});
  }

  unpublishEmailTemplate(envId: string, emailType: string): Observable<EmailTemplateDto> {
    return this.http.post<EmailTemplateDto>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/email-templates/${emailType}/unpublish`, {});
  }

  // Story 26.3 — CloudWatch Logs query API (Story 26.2 backend). All three map
  // snake_case query params + snake_case response → camelCase. next_token is
  // omitted (not null) when there is no further page.
  getLogs(envId: string, params: LogQueryParams): Observable<LogsResponse> {
    return this.http
      .get<RawLogsResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/logs`, { params: logHttpParams(params) })
      .pipe(map(mapLogsResponse));
  }

  getUserLogs(envId: string, userId: string, params: LogQueryParams): Observable<LogsResponse> {
    return this.http
      .get<RawLogsResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/logs/users/${userId}`, { params: logHttpParams(params) })
      .pipe(map(mapLogsResponse));
  }

  getClientLogs(envId: string, clientId: string, params: LogQueryParams): Observable<LogsResponse> {
    return this.http
      .get<RawLogsResponse>(`${environment.tenantAdminApiUrl}/admin/environments/${envId}/logs/clients/${clientId}`, { params: logHttpParams(params) })
      .pipe(map(mapLogsResponse));
  }

  // Story 38.7 — terms management + per-client eligibility (AC1–AC3).
  listTerms(): Observable<TermListItem[]> {
    return this.http.get<TermListItem[]>(`${environment.tenantAdminApiUrl}/admin/terms`);
  }

  getTerm(number: string): Observable<TermDocument> {
    return this.http.get<TermDocument>(`${environment.tenantAdminApiUrl}/admin/terms/${encodeURIComponent(number)}`);
  }

  createTerm(body: CreateTermRequest): Observable<TermVersion> {
    return this.http.post<TermVersion>(`${environment.tenantAdminApiUrl}/admin/terms`, body);
  }

  publishTermVersion(number: string, body: PublishVersionRequest): Observable<TermVersion> {
    return this.http.post<TermVersion>(`${environment.tenantAdminApiUrl}/admin/terms/${encodeURIComponent(number)}/versions`, body);
  }

  patchTermVersion(number: string, version: number, activeTo: string): Observable<TermVersion> {
    return this.http.patch<TermVersion>(
      `${environment.tenantAdminApiUrl}/admin/terms/${encodeURIComponent(number)}/versions/${version}`,
      { activeTo },
    );
  }

  getEligibility(number: string): Observable<Eligibility> {
    return this.http.get<Eligibility>(`${environment.tenantAdminApiUrl}/admin/terms/${encodeURIComponent(number)}/eligibility`);
  }

  setEligibility(number: string, clientIds: string[]): Observable<Eligibility> {
    return this.http.put<Eligibility>(`${environment.tenantAdminApiUrl}/admin/terms/${encodeURIComponent(number)}/eligibility`, { clientIds });
  }

  // Story 38.6 — compliance endpoints (AC1/AC2/AC3).
  // fetchCompliance returns stale docs (not-yet-accepted latest active versions).
  // fetchConsentPermissions returns all active permissions with current decision.
  // postAcceptances records per-doc decisions + drives suspension/reinstatement.
  fetchCompliance(): Observable<ComplianceDoc[]> {
    return this.http.get<ComplianceDoc[]>(`${environment.tenantAdminApiUrl}/admin/compliance`);
  }

  fetchConsentPermissions(): Observable<ConsentPermission[]> {
    return this.http.get<ConsentPermission[]>(`${environment.tenantAdminApiUrl}/admin/compliance/permissions`);
  }

  postAcceptances(body: AcceptancesRequest): Observable<AcceptancesResult> {
    return this.http.post<AcceptancesResult>(`${environment.tenantAdminApiUrl}/admin/compliance/acceptances`, body);
  }

  getMyTermAcceptances(): Observable<TermAcceptanceStatus[]> {
    return this.http.get<TermAcceptanceStatus[]>(`${environment.tenantAdminApiUrl}/admin/compliance/acceptances`);
  }

  getAcceptedTermsHistory(): Observable<AcceptedTermHistoryItem[]> {
    return this.http.get<AcceptedTermHistoryItem[]>(`${environment.tenantAdminApiUrl}/admin/compliance/accepted-terms`);
  }
}
